import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { Rect } from '../../types/diagram.ts'
import { CustomizeDiagramViewPortContext, DiagramViewPortContext } from './DiagramViewPortContext.tsx'

export const DiagramViewPortProvider = ({ children }: PropsWithChildren) => {
    const [viewPort, setViewPort] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 })
    const [initialViewPort, setInitialViewPort] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 })
    const [zoom, setZoom] = useState<number>(1)
    const [container, setContainer] = useState<SVGSVGElement | undefined>(undefined)
    const [htmlContainer, setHTMLContainer] = useState<HTMLElement | undefined>(undefined)

    const resetViewPort = useCallback(() => {
        setViewPort(() => initialViewPort)
        setZoom(() => 1)
    }, [setViewPort, initialViewPort])

    // Toolbar zoom: scale around the centre of what is visible, never past the initial fit
    const zoomBy = useCallback((step: number) => {
        const level = Math.max(1, zoom + step)
        if (level === zoom) {
            return
        }
        if (level === 1) {
            resetViewPort()
            return
        }

        const width = Math.min(initialViewPort.width, viewPort.width * (zoom / level))
        const height = Math.min(initialViewPort.height, viewPort.height * (zoom / level))
        setViewPort(() => ({
            x: Math.max(initialViewPort.x, viewPort.x + (viewPort.width - width) / 2),
            y: Math.max(initialViewPort.y, viewPort.y + (viewPort.height - height) / 2),
            width,
            height
        }))
        setZoom(() => level)
    }, [zoom, viewPort, initialViewPort, resetViewPort])

    const zoomIn = useCallback(() => zoomBy(1), [zoomBy])
    const zoomOut = useCallback(() => zoomBy(-1), [zoomBy])

    const diagramViewPortContextValue = useMemo(
        () => ({
            viewPort,
            initialViewPort,
            zoom,
            container,
            htmlContainer
        }),
        [viewPort, initialViewPort, zoom, container, htmlContainer]
    )

    const customizeDiagramViewPortContextValue = useMemo(
        () => ({
            setViewPort,
            setInitialViewPort,
            setZoom,
            setContainer,
            resetViewPort,
            zoomIn,
            zoomOut,
            setHTMLContainer
        }),
        [zoomIn, zoomOut, resetViewPort, setViewPort, setInitialViewPort, setZoom, setContainer]
    )

    return <DiagramViewPortContext.Provider value={diagramViewPortContextValue}>
        <CustomizeDiagramViewPortContext.Provider value={customizeDiagramViewPortContextValue}>
            {children}
        </CustomizeDiagramViewPortContext.Provider>
    </DiagramViewPortContext.Provider>
}
